/* eslint-disable react-hooks/set-state-in-effect -- legitimate setState in
   mount-time data hydration; admin handlers also call setState. */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { fetchPublicAppPages } from "@/lib/appPagesApi";

const AppPagesContext = createContext(null);

const CACHE_KEY = "pf_app_pages_cache_v1";
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const loadCached = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data || !parsed.ts) return null;
    if (Date.now() - parsed.ts > CACHE_MAX_AGE_MS) return null;
    return parsed.data;
  } catch (_e) {
    return null;
  }
};

const saveCached = (data) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, ts: Date.now() })
    );
  } catch (_e) {
    // ignore
  }
};

export const AppPagesProvider = ({ children }) => {
  const cached = loadCached();
  const [pages, setPages] = useState(Array.isArray(cached) ? cached : []);
  const [loading, setLoading] = useState(true);
  const [hasCached] = useState(Boolean(cached));

  const refresh = useCallback(async () => {
    try {
      const data = await fetchPublicAppPages();
      setPages(Array.isArray(data) ? data : []);
      saveCached(data);
    } catch (_e) {
      // keep existing
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const findBySlug = useCallback(
    (slug) => pages.find((p) => p.slug === slug),
    [pages]
  );

  return (
    <AppPagesContext.Provider
      value={{ pages, loading, hasCached, refresh, findBySlug, setPages }}
    >
      {children}
    </AppPagesContext.Provider>
  );
};

export const useAppPages = () => {
  const ctx = useContext(AppPagesContext);
  if (!ctx) throw new Error("useAppPages must be used within AppPagesProvider");
  return ctx;
};
